// GA4 + Meta Pixel, loaded only when their IDs are configured.
//
// Both scripts are injected at runtime rather than hardcoded in index.html so
// that a build without the env vars ships no third-party tags at all — the
// prerendered HTML stays identical and no request goes out.
//
// Vercel Analytics is mounted separately in main.tsx and is not routed through
// here; trackEvent fans out to it too so a call site only fires one function.

import { track as vercelTrack } from '@vercel/analytics';

type EventParams = Record<string, string | number | boolean>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[] };
    _fbq?: unknown;
  }
}

const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined;
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

export const hasGA4 = Boolean(GA4_ID);
export const hasPixel = Boolean(PIXEL_ID);

let initialized = false;

function loadGA4(id: string) {
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // gtag must push `arguments` itself — a rest-array push breaks GA's parser.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // Routing is client-side, so page_view is sent manually on each navigation.
  window.gtag('config', id, { send_page_view: false });
}

function loadPixel(id: string) {
  /* eslint-disable */
  // Meta's official snippet, transcribed. The queue shim buffers calls made
  // before fbevents.js finishes loading.
  const n: any = (window.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  /* eslint-enable */

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);

  window.fbq!('init', id);
}

/** Injects whichever tags are configured. Safe to call more than once. */
export function initAnalytics() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  if (GA4_ID) loadGA4(GA4_ID);
  if (PIXEL_ID) loadPixel(PIXEL_ID);
}

/** Sends a page_view to every configured destination. */
export function trackPageView(path: string, title?: string) {
  if (GA4_ID) {
    window.gtag?.('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: title ?? document.title,
    });
  }
  if (PIXEL_ID) {
    window.fbq?.('track', 'PageView');
  }
}

/**
 * Custom event. `name` should be a GA4-style snake_case name; Meta receives it
 * as a custom event via trackCustom, which accepts arbitrary names.
 */
export function trackEvent(name: string, params: EventParams = {}) {
  vercelTrack(name, params);
  if (GA4_ID) window.gtag?.('event', name, params);
  if (PIXEL_ID) window.fbq?.('trackCustom', name, params);
}

/**
 * A completed inquiry — the only real conversion on the site.
 * Uses each platform's standard lead event so it can be optimized against in
 * Google Ads and Meta Ads without extra configuration.
 */
export function trackLead(params: EventParams = {}) {
  vercelTrack('contact_form_submitted', params);
  if (GA4_ID) window.gtag?.('event', 'generate_lead', params);
  if (PIXEL_ID) window.fbq?.('track', 'Lead', params);
}

/** A click on a direct contact channel (LINE / phone / email). */
export function trackContactClick(channel: 'line' | 'phone' | 'email', location: string) {
  trackEvent('contact_channel_click', { channel, location });
  if (PIXEL_ID) window.fbq?.('track', 'Contact', { channel });
}

/** A click on a CTA that routes toward the contact page. */
export function trackCtaClick(location: string) {
  trackEvent('cta_click', { location });
}
